/* @flow */

import {
  getConversationId,
  getConversationMessages,
} from '@e180/domain/lib/conversation/util';
import { assertObject } from '@e180/util/lib/assert';
import { isObject } from '@e180/util/lib/type';

import * as types from 'src/shared/app/conversation/action/uiConversationActionTypes';
import { createReducer } from 'src/shared/core/util/reducerUtils';
import * as entityTypes from 'src/shared/domain/base/action/entityActionTypes';
import {
  getConversationLastReadMessage,
  hasConversationMessages,
} from 'src/shared/domain/conversation/util/conversationUtil';
import * as conversationTypes from 'src/shared/domain/conversation/action/conversationActionTypes';
import { getMessageId } from 'src/shared/domain/conversation/util/messageUtils';

/*
 |------------------------------------------------------------------------------
 | Initial state
 |------------------------------------------------------------------------------
 */

const initialState = {
  backUrl: '',
  isBeingTypedIn: false,
  pendingAction: null,
  unidentifiedError: '',
  error: {},
  isInitialized: {},
  lastReadMessage: {},
  animatedMessage: {},
};

/*
 |------------------------------------------------------------------------------
 | Reducers
 |------------------------------------------------------------------------------
 */

const setBackUrl = (state: Object, { url }: Object) => ({
  ...state,
  backUrl: url,
});

const resetBackUrl = (state: Object) => ({
  ...state,
  backUrl: '',
});

const markAsBeingTypedIn = (state: Object) => ({
  ...state,
  isBeingTypedIn: true,
});

const unmarkAsBeingTypedIn = (state: Object) => ({
  ...state,
  isBeingTypedIn: false,
});

const setPendingAction = (state: Object, { action }: Object) => ({
  ...state,
  pendingAction: action,
});

const resetPendingAction = (state: Object) => ({
  ...state,
  pendingAction: null,
});

const setUnidentifiedError = (state: Object, { exception }: Object) => {
  assertObject(exception, 'exception');

  const { json } = exception;

  return {
    ...state,
    unidentifiedError: isObject(json) ? json.detail : '',
  };
};

const resetUnidentifiedError = (state: Object) => ({
  ...state,
  unidentifiedError: '',
});

const setError = (state: Object, { exception, conversationId }: Object) => {
  assertObject(exception, 'exception');

  const { json } = exception;
  const errorMessages = isObject(json) ? json.action : '';

  if (errorMessages) {
    return {
      ...state,
      error: {
        ...state.error,
        [conversationId]: Array.isArray(errorMessages)
          ? errorMessages.join('\n')
          : errorMessages,
      },
    };
  }

  return state;
};

const resetError = (state: Object, { conversationId }: Object) => ({
  ...state,
  error: {
    ...state.error,
    [conversationId]: '',
  },
});

const markAsInitialized = (state: Object, { conversation }: Object) => ({
  ...state,
  isInitialized: {
    ...state.isInitialized,
    [getConversationId(conversation)]: true,
  },
});

const unmarkAsInitialized = (state: Object, { conversation }: Object) => ({
  ...state,
  isInitialized: {
    ...state.isInitialized,
    [getConversationId(conversation)]: false,
  },
});

const initLastReadMessage = (state: Object, { entities }: Object) => {
  assertObject(entities, 'entities');

  const { conversations } = entities;

  if (conversations) {
    assertObject(conversations, 'entities.conversations');

    const { lastReadMessage } = state;

    for (const conversationId in conversations) {
      // If last read message is already set, do nothing
      if (lastReadMessage[conversationId]) {
        return state;
      }

      const conversation = conversations[conversationId];

      if (hasConversationMessages(conversation)) {
        const message = getConversationLastReadMessage(conversation);

        if (message) {
          return {
            ...state,
            lastReadMessage: {
              ...lastReadMessage,
              [conversationId]: getMessageId(message),
            },
          };
        }
      }
    }
  }

  return state;
};

// conversation.messages should be necessarily defined at this point
const showNextMessage = (state: Object, { conversation }: Object) => {
  const { animatedMessage, lastReadMessage } = state;
  const conversationId = getConversationId(conversation);

  if (animatedMessage[conversationId]) {
    // If conversation is already animating a message, don't do anything (next
    // message will be shown once the animation is done)
    return state;
  }

  const lastReadMessageId = lastReadMessage[conversationId];
  const messages = getConversationMessages(conversation) || [];
  const lastReadIndex = messages.findIndex(
    (message) => getMessageId(message) === lastReadMessageId,
  );
  const newIndex = lastReadIndex + 1;

  if (newIndex < messages.length) {
    // If all messages haven't been read, read the next one and freeze the
    // conversation
    return {
      ...state,
      lastReadMessage: {
        ...lastReadMessage,
        [conversationId]: getMessageId(messages[newIndex]),
      },
      animatedMessage: {
        ...animatedMessage,
        [conversationId]: getMessageId(messages[newIndex]),
      },
    };
  }

  // If all messages have been read, just defrost the conversation
  return {
    ...state,
    animatedMessage: {
      ...animatedMessage,
      [conversationId]: null,
    },
  };
};

const resetAnimatedMessage = (state: Object, { conversation }: Object) => ({
  ...state,
  animatedMessage: {
    ...state.animatedMessage,
    [getConversationId(conversation)]: null,
  },
});

export default createReducer(initialState, {
  [types.SET_CONVERSATION_BACK_URL]: setBackUrl,
  [types.RESET_CONVERSATION_BACK_URL]: resetBackUrl,
  [types.MARK_CONVERSATION_AS_BEING_TYPED_IN]: markAsBeingTypedIn,
  [types.UNMARK_CONVERSATION_AS_BEING_TYPED_IN]: unmarkAsBeingTypedIn,
  [types.SHOW_ACTION_CONFIRMATION_MODAL]: setPendingAction,
  [types.DISMISS_ACTION_CONFIRMATION_MODAL]: resetPendingAction,
  [types.MARK_CONVERSATION_AS_INITIALIZED]: markAsInitialized,
  [types.UNMARK_CONVERSATION_AS_INITIALIZED]: unmarkAsInitialized,
  [conversationTypes.FETCH_CONVERSATION_FAILURE]: setUnidentifiedError,
  [types.DISMISS_UNIDENTIFIED_CONVERSATION_ERROR]: resetUnidentifiedError,
  [types.DISMISS_ACTION_CONVERSATION_ERROR]: resetError,
  [conversationTypes.REPLY_TO_CONVERSATION_FAILURE]: setError,
  [conversationTypes.REPLY_TO_CONVERSATION_REQUEST]: resetError,
  [entityTypes.PATCH_ENTITIES]: initLastReadMessage,
  [types.SHOW_NEXT_MESSAGE]: showNextMessage,
  [types.DEFROST_CONVERSATION]: resetAnimatedMessage,
});
